import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Mail, Lock, User, ArrowRight, Loader2 } from "lucide-react";
import { trackSignUp, trackStartTrial, ga4Event } from "@/utils/tracking";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LoginPage() {
  const [searchParams] = useSearchParams();
  const [isRegister, setIsRegister] = useState(searchParams.get("mode") === "register");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showReset, setShowReset] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [resetSent, setResetSent] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const { login, register, loginWithGoogle, user, verifyMfa } = useAuth();
  const navigate = useNavigate();

  // Check for tokens in URL
  const resetToken = searchParams.get("reset");
  const verifyToken = searchParams.get("verify");
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [resetConfirmMsg, setResetConfirmMsg] = useState("");
  const [resetConfirmError, setResetConfirmError] = useState("");
  const [resetConfirmLoading, setResetConfirmLoading] = useState(false);
  const [verifyMsg, setVerifyMsg] = useState("");
  const [verifyError, setVerifyError] = useState("");
  const [mfaRequired, setMfaRequired] = useState(false);
  const [mfaCode, setMfaCode] = useState("");
  const [mfaLoading, setMfaLoading] = useState(false);
  const [mfaError, setMfaError] = useState("");

  useEffect(() => {
    if (user) navigate("/dashboard", { replace: true });
  }, [user, navigate]);

  // Auto-verify email on mount if verify token present
  useEffect(() => {
    if (verifyToken && !verifyMsg && !verifyError) {
      axios.post(`${API}/auth/verify-email`, { token: verifyToken })
        .then(({ data }) => setVerifyMsg(data.message))
        .catch(err => setVerifyError(err.response?.data?.detail || "Verification failed"));
    }
  }, [verifyToken, verifyMsg, verifyError]);

  const handleResetRequest = async (e) => {
    e.preventDefault();
    if (!resetEmail.trim()) return;
    setResetLoading(true);
    try {
      await axios.post(`${API}/auth/password-reset/request`, { email: resetEmail.trim() });
      setResetSent(true);
    } catch {}
    setResetLoading(false);
  };

  const handleResetConfirm = async (e) => {
    e.preventDefault();
    setResetConfirmError("");
    if (newPassword.length < 8) { setResetConfirmError("Password must be at least 8 characters"); return; }
    if (newPassword !== confirmNewPassword) { setResetConfirmError("Passwords don't match"); return; }
    setResetConfirmLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/password-reset/confirm`, { token: resetToken, new_password: newPassword });
      setResetConfirmMsg(data.message);
    } catch (err) {
      setResetConfirmError(err.response?.data?.detail || "Reset failed. The link may have expired.");
    }
    setResetConfirmLoading(false);
  };

  // If we have a reset token, show the new password form
  if (resetToken) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ background: "linear-gradient(135deg, #F97316 0%, #D946EF 50%, #F97316 100%)" }}>
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <a href="/" className="inline-block">
              <img src="/adbit-logo.png" alt="AdBit" className="h-10 w-auto brightness-0 invert mx-auto" />
            </a>
          </div>
          <div className="bg-white rounded-2xl shadow-2xl p-8">
            <h2 className="text-2xl font-bold mb-1 text-center" style={{ fontFamily: "Outfit, sans-serif", color: "#0F172A" }}>Set New Password</h2>
            <p className="text-sm text-slate-500 text-center mb-6">Enter your new password below</p>
            {resetConfirmMsg ? (
              <div className="text-center">
                <div className="w-12 h-12 rounded-full bg-emerald-50 flex items-center justify-center mx-auto mb-3">
                  <span className="text-emerald-600 text-lg">✓</span>
                </div>
                <p className="text-sm text-emerald-700 mb-4">{resetConfirmMsg}</p>
                <Button onClick={() => navigate("/login")} className="gradient-btn w-full h-11 rounded-xl font-semibold">Sign In</Button>
              </div>
            ) : (
              <form onSubmit={handleResetConfirm} className="space-y-3">
                <div className="relative">
                  <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input type="password" placeholder="New password (min 8 chars)" value={newPassword} onChange={e => setNewPassword(e.target.value)} className="pl-10 h-11 rounded-xl" required minLength={8} />
                </div>
                <div className="relative">
                  <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input type="password" placeholder="Confirm new password" value={confirmNewPassword} onChange={e => setConfirmNewPassword(e.target.value)} className="pl-10 h-11 rounded-xl" required minLength={8} />
                </div>
                {resetConfirmError && <p className="text-sm text-red-500 text-center">{resetConfirmError}</p>}
                <Button type="submit" disabled={resetConfirmLoading} className="gradient-btn w-full h-11 rounded-xl font-semibold">
                  {resetConfirmLoading ? <Loader2 size={16} className="animate-spin" /> : "Reset Password"}
                </Button>
              </form>
            )}
          </div>
        </div>
      </div>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (isRegister && !termsAccepted) {
      setError("You must accept the Terms and Conditions and Privacy Policy to create an account.");
      return;
    }
    setLoading(true);
    const result = isRegister
      ? await register(email, password, name, marketingConsent)
      : await login(email, password);
    setLoading(false);
    if (result.mfa_required) {
      setMfaRequired(true);
      return;
    }
    if (result.success) {
      if (isRegister) {
        trackSignUp(email);
        trackStartTrial(email);
        ga4Event("sign_up", { method: "email" });
      } else {
        ga4Event("login", { method: "email" });
      }
      navigate("/dashboard", { replace: true });
    } else {
      setError(result.error);
    }
  };

  const handleMfaVerify = async (e) => {
    e.preventDefault();
    setMfaError("");
    setMfaLoading(true);
    const result = await verifyMfa(mfaCode);
    setMfaLoading(false);
    if (result.success) {
      navigate("/dashboard", { replace: true });
    } else {
      setMfaError(result.error || "Invalid code");
    }
  };

  // Email verification screen
  if (verifyToken) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ background: "linear-gradient(135deg, #F97316 0%, #D946EF 50%, #F97316 100%)" }}>
        <div className="w-full max-w-md">
          <div className="text-center mb-8"><a href="/" className="inline-block"><img src="/adbit-logo.png" alt="AdBit" className="h-10 w-auto brightness-0 invert mx-auto" /></a></div>
          <div className="bg-white rounded-2xl shadow-2xl p-8 text-center">
            {verifyMsg ? (
              <>
                <div className="w-14 h-14 rounded-full bg-emerald-50 flex items-center justify-center mx-auto mb-4"><span className="text-emerald-600 text-2xl">✓</span></div>
                <h2 className="text-xl font-bold text-slate-800 mb-2" style={{ fontFamily: "Outfit, sans-serif" }}>Email Verified!</h2>
                <p className="text-sm text-slate-500 mb-6">{verifyMsg}</p>
                <Button onClick={() => navigate("/login")} className="gradient-btn w-full h-11 rounded-xl font-semibold">Continue to Sign In</Button>
              </>
            ) : verifyError ? (
              <>
                <div className="w-14 h-14 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-4"><span className="text-red-500 text-2xl">✕</span></div>
                <h2 className="text-xl font-bold text-slate-800 mb-2" style={{ fontFamily: "Outfit, sans-serif" }}>Verification Failed</h2>
                <p className="text-sm text-red-500 mb-6">{verifyError}</p>
                <Button onClick={() => navigate("/login")} variant="outline" className="rounded-xl">Back to Login</Button>
              </>
            ) : (
              <>
                <Loader2 size={32} className="animate-spin text-orange-500 mx-auto mb-4" />
                <p className="text-sm text-slate-500">Verifying your email...</p>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  // MFA verification screen
  if (mfaRequired) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ background: "linear-gradient(135deg, #F97316 0%, #D946EF 50%, #F97316 100%)" }}>
        <div className="w-full max-w-md">
          <div className="text-center mb-8"><a href="/" className="inline-block"><img src="/adbit-logo.png" alt="AdBit" className="h-10 w-auto brightness-0 invert mx-auto" /></a></div>
          <div className="bg-white rounded-2xl shadow-2xl p-8">
            <div className="text-center mb-6">
              <div className="w-14 h-14 rounded-2xl bg-orange-50 flex items-center justify-center mx-auto mb-3"><Lock size={24} className="text-orange-500" /></div>
              <h2 className="text-xl font-bold text-slate-800" style={{ fontFamily: "Outfit, sans-serif" }}>Two-Factor Authentication</h2>
              <p className="text-sm text-slate-500 mt-1">Enter the 6-digit code from your authenticator app</p>
            </div>
            <form onSubmit={handleMfaVerify} className="space-y-4">
              <input type="text" placeholder="000000" value={mfaCode} onChange={e => setMfaCode(e.target.value.replace(/\D/g, ""))} maxLength={6}
                autoFocus className="w-full h-14 text-center text-2xl font-mono tracking-[0.5em] rounded-xl border border-slate-200 outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-400/20" />
              {mfaError && <p className="text-sm text-red-500 text-center">{mfaError}</p>}
              <Button type="submit" disabled={mfaLoading || mfaCode.length !== 6} className="gradient-btn w-full h-11 rounded-xl font-semibold">
                {mfaLoading ? <Loader2 size={16} className="animate-spin" /> : "Verify"}
              </Button>
            </form>
            <button onClick={() => { setMfaRequired(false); setMfaCode(""); setMfaError(""); }} className="text-xs text-slate-400 mt-4 block mx-auto hover:text-slate-600">Back to Login</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: "linear-gradient(135deg, #F97316 0%, #D946EF 50%, #F97316 100%)" }}>
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <a href="/" className="inline-block">
            <img src="/adbit-logo.png" alt="AdBit" className="h-10 w-auto brightness-0 invert mx-auto" />
          </a>
        </div>

        {/* Card */}
        <div className="bg-white rounded-2xl shadow-2xl p-8" data-testid="login-card">
          <h2 className="text-2xl font-bold mb-1 text-center" style={{ fontFamily: "Outfit, sans-serif", color: "#0F172A" }}>
            {isRegister ? "Create Account" : "Welcome Back"}
          </h2>
          <p className="text-sm text-slate-500 text-center mb-6">
            {isRegister ? "Start analyzing ads with AI" : "Sign in to your account"}
          </p>

          {/* Google OAuth */}
          <Button
            data-testid="google-login-btn"
            variant="outline"
            className="w-full h-11 rounded-xl border-slate-300 text-slate-700 font-medium mb-4"
            onClick={loginWithGoogle}
          >
            <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
            Continue with Google
          </Button>

          <div className="flex items-center gap-3 mb-4">
            <div className="flex-1 h-px bg-slate-200" />
            <span className="text-xs text-slate-400">or</span>
            <div className="flex-1 h-px bg-slate-200" />
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-3">
            {isRegister && (
              <div className="relative">
                <User size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  data-testid="register-name-input"
                  placeholder="Full Name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="pl-10 h-11 rounded-xl"
                  required
                />
              </div>
            )}
            <div className="relative">
              <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                data-testid="login-email-input"
                type="email"
                placeholder="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="pl-10 h-11 rounded-xl"
                required
              />
            </div>
            <div className="relative">
              <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                data-testid="login-password-input"
                type="password"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="pl-10 h-11 rounded-xl"
                required
                minLength={8}
              />
            </div>

            {!isRegister && (
              <div className="text-right">
                <button type="button" onClick={() => setShowReset(true)} className="text-xs text-orange-600 hover:underline">
                  Forgot password?
                </button>
              </div>
            )}

            {isRegister && (
              <div className="space-y-2 pt-1">
                <label className="flex items-start gap-2 cursor-pointer">
                  <input type="checkbox" checked={termsAccepted} onChange={e => setTermsAccepted(e.target.checked)}
                    className="mt-1 w-4 h-4 rounded border-slate-300 text-orange-500 focus:ring-orange-400" />
                  <span className="text-xs text-slate-500 leading-relaxed">
                    I accept the <a href="/terms" target="_blank" className="text-orange-600 hover:underline font-medium">Terms and Conditions</a> and <a href="/privacy" target="_blank" className="text-orange-600 hover:underline font-medium">Privacy Policy</a>
                  </span>
                </label>
                <label className="flex items-start gap-2 cursor-pointer">
                  <input type="checkbox" checked={marketingConsent} onChange={e => setMarketingConsent(e.target.checked)}
                    className="mt-1 w-4 h-4 rounded border-slate-300 text-orange-500 focus:ring-orange-400" />
                  <span className="text-xs text-slate-500 leading-relaxed">
                    I agree to receive marketing communications about new features and updates
                  </span>
                </label>
              </div>
            )}

            {error && <p data-testid="auth-error" className="text-sm text-red-500 text-center">{error}</p>}

            <Button
              data-testid="login-submit-btn"
              type="submit"
              disabled={loading}
              className="gradient-btn w-full h-11 rounded-xl font-semibold"
            >
              <span className="flex items-center gap-2">
                {loading ? "Please wait..." : isRegister ? "Create Account" : "Sign In"}
                <ArrowRight size={16} />
              </span>
            </Button>
          </form>

          <p className="text-center text-sm text-slate-500 mt-4">
            {isRegister ? "Already have an account?" : "Don't have an account?"}{" "}
            <button
              data-testid="toggle-auth-mode"
              onClick={() => { setIsRegister(!isRegister); setError(""); }}
              className="text-orange-600 font-medium hover:underline"
            >
              {isRegister ? "Sign In" : "Create Account"}
            </button>
          </p>
        </div>
      </div>

      {/* Forgot password modal */}
      {showReset && (
        <div className="fixed inset-0 bg-black/30 z-50 flex items-center justify-center p-4" onClick={() => { setShowReset(false); setResetSent(false); }}>
          <div className="bg-white rounded-2xl w-full max-w-sm p-6 shadow-2xl" onClick={e => e.stopPropagation()}>
            {resetSent ? (
              <div className="text-center">
                <div className="w-12 h-12 rounded-full bg-emerald-50 flex items-center justify-center mx-auto mb-3">
                  <Mail size={20} className="text-emerald-600" />
                </div>
                <h3 className="text-base font-bold text-slate-800 mb-2" style={{ fontFamily: "Outfit, sans-serif" }}>Check your email</h3>
                <p className="text-sm text-slate-500 mb-4">If an account exists for {resetEmail}, we've sent a password reset link.</p>
                <Button onClick={() => { setShowReset(false); setResetSent(false); }} variant="outline" className="rounded-xl text-sm">Back to Login</Button>
              </div>
            ) : (
              <>
                <h3 className="text-base font-bold text-slate-800 mb-1" style={{ fontFamily: "Outfit, sans-serif" }}>Reset Password</h3>
                <p className="text-sm text-slate-500 mb-4">Enter your email and we'll send you a reset link</p>
                <form onSubmit={handleResetRequest} className="space-y-3">
                  <div className="relative">
                    <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <Input type="email" placeholder="Email address" value={resetEmail} onChange={e => setResetEmail(e.target.value)} className="pl-10 h-11 rounded-xl" required />
                  </div>
                  <Button type="submit" disabled={resetLoading} className="gradient-btn w-full h-11 rounded-xl font-semibold">
                    {resetLoading ? <Loader2 size={16} className="animate-spin" /> : "Send Reset Link"}
                  </Button>
                </form>
                <button onClick={() => setShowReset(false)} className="text-xs text-slate-400 mt-3 block mx-auto hover:text-slate-600">Cancel</button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
